// pages/FormViewerPage/hooks/useFormViewer/effectsSync.ts — Selection sync effects
import { useEffect } from 'react';
import type { FormViewerState } from './state';
import type { FormViewerComputed } from './computed';

export function useFormViewerSyncEffects(s: FormViewerState, c: FormViewerComputed, _isSimpleEmbed: boolean = false) {
    const { form, setSelectedOptions, urlVariantParams, shippingCostRef, selectedPaymentKey, setSelectedPaymentKey, loading, formPagePixelConfigs, setPixelsLoaded } = s;
    const { displayOptions, shippingCost, sortedPaymentKeys } = c;

    // Select options on displayOptions change
    useEffect(() => {
        if (displayOptions.length === 0) return;

        const initialOptions: Record<string, string> = {};
        const leadVariant = (urlVariantParams.__lead_variant__ || '').toLowerCase();

        displayOptions.forEach(option => {
            let picked = '';
            const u = urlVariantParams[option.name];
            if (u && option.values.includes(u)) picked = u;
            if (!picked && leadVariant) {
                const hit = option.values.find(v => (v || '').toLowerCase() === leadVariant || (v || '').toLowerCase().includes(leadVariant));
                if (hit) picked = hit;
            }
            if (!picked && form?.defaultVariant && option.values.includes(form.defaultVariant)) picked = form.defaultVariant;
            if (!picked && option.values.length > 0) picked = option.values[0];
            if (picked) initialOptions[option.name] = picked;
        });
        setSelectedOptions(prev => {
            const hasUrl = leadVariant || displayOptions.some(o => o.name in urlVariantParams);
            if (!hasUrl && Object.keys(prev).length === displayOptions.length && displayOptions.every(o => prev[o.name])) return prev;
            return { ...prev, ...initialOptions };
        });
    }, [displayOptions, urlVariantParams, form?.defaultVariant, form?.variantCombinations]);

    useEffect(() => { shippingCostRef.current = shippingCost; }, [shippingCost]);

    // Payment sorting
    useEffect(() => {
        if (sortedPaymentKeys.length > 0 && selectedPaymentKey && !sortedPaymentKeys.includes(selectedPaymentKey)) {
            setSelectedPaymentKey(sortedPaymentKeys[0]);
        }
    }, [sortedPaymentKeys, selectedPaymentKey]);

    // Set pixels loaded after form loads
    useEffect(() => {
        if (form && !loading && formPagePixelConfigs.length > 0) setPixelsLoaded(true);
    }, [form, loading, formPagePixelConfigs]);
}
